"use client";

import { useState } from "react";
import { createClient } from '@/lib/supabase/client';
import TurnstileWidget from '@/components/ui/TurnstileWidget';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export default function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [isUser, setIsUser] = useState(true);
  const [isDriver, setIsDriver] = useState(true);
  const [isNews, setIsNews] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!turnstileToken) {
      setError('Please complete the security verification before subscribing.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Validate Turnstile token server-side
      const verifyRes = await fetch('/api/turnstile/verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token: turnstileToken }),
      });
      const verifyData = await verifyRes.json();

      if (!verifyData.success) {
        setError('Security verification failed. Please refresh and try again.');
        setTurnstileToken(null);
        setSubmitting(false);
        return;
      }

      const supabase = createClient();

      const { error: insertError } = await supabase
        .from('newsletter_signups')
        .insert({
          email,
          wants_news: isNews,
          wants_customer_updates: isUser,
          wants_driver_updates: isDriver,
        });

      if (insertError) {
        if (isSchemaError(insertError)) {
          console.error('Schema error:', insertError.message);
          throw insertError;
        } else {
          if (insertError.code === '23505') {
            setError('This email is already subscribed.');
          } else {
            setError('Failed to subscribe. Please try again.');
          }
          return;
        }
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Newsletter signup error:', err.message);
      setError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="newsletter" className="newsletter-section py-24 relative overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-brand-purple/10 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-brand-green/10 translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="bg-white rounded-5xl shadow-purple-lg p-10 md:p-16">
          <div className="text-center mb-10">
            <div className="section-label mx-auto w-fit mb-6">Stay Updated</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy leading-tight mb-4">
              Join the Urban Logistics Revolution
            </h2>
            <p className="text-body-text text-lg">
              To stay updated on all things moving at GoGedit.
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-brand-green/15 flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="font-heading text-xl font-bold text-deep-navy mb-2">You&apos;re in!</h3>
              <p className="text-body-text">Thanks for joining the GoGedit community.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl text-center">
                  {error}
                </div>
              )}

              {/* Email input */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-text">
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-mail"
                  className="w-full pl-12 pr-4 py-4 border-2 border-border-gray rounded-2xl font-medium text-deep-navy placeholder:text-muted-text focus:outline-none focus:border-brand-purple hover:border-brand-purple/50 hover:shadow-md transition-all duration-300"
                />
              </div>

              {/* Checkboxes */}
              <div className="flex flex-wrap gap-6 justify-center">
                {[
                  { label: "GoGedit News", value: isNews, setter: setIsNews },
                  { label: "Customer", value: isUser, setter: setIsUser },
                  { label: "Driver", value: isDriver, setter: setIsDriver },
                ].map((item) => (
                  <label
                    key={item.label}
                    className="flex items-center gap-3 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={item.value}
                      onChange={(e) => item.setter(e.target.checked)}
                      className="checkbox-custom"
                    />
                    <span className="text-sm font-medium text-secondary-text group-hover:text-deep-navy transition-colors">
                      {item.label}
                    </span>
                  </label>
                ))}
              </div>

              {/* Turnstile */}
              <div>
                <TurnstileWidget
                  onSuccess={(token) => {
                    setTurnstileToken(token);
                    setTurnstileError(false);
                  }}
                  onError={() => {
                    setTurnstileToken(null);
                    setTurnstileError(true);
                  }}
                  onExpire={() => {
                    setTurnstileToken(null);
                  }}
                />
                {turnstileError && (
                  <p className="text-red-600 text-sm mt-2 text-center">
                    Security verification encountered an error. Please refresh the page.
                  </p>
                )}
              </div>

              {/* Submit */}
              <div className="text-center">
                <button type="submit" disabled={submitting || !turnstileToken} className="btn-primary px-12 py-4 text-base disabled:opacity-50">
                  {submitting ? 'Subscribing...' : 'Subscribe'}
                  {!submitting && (
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  )}
                </button>
                <p className="text-muted-text text-sm mt-4">Don&apos;t worry, we won&apos;t spam you.</p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}